import * as React from 'react';

/**
 * Hook that fixes wheel scrolling for elements inside Radix modal contexts.
 *
 * Radix UI's Dialog/Popover components apply scroll-lock to the body to prevent
 * background scrolling. This inadvertently blocks wheel events from scrolling
 * nested scrollable elements. This hook attaches a native wheel listener with
 * { passive: false } to manually handle scrolling and prevent event propagation.
 *
 * @param scrollRef - Ref to the scrollable element
 *
 * @example
 * const scrollRef = useRef<HTMLDivElement>(null);
 * useModalScrollFix(scrollRef);
 * return <div ref={scrollRef} className="overflow-y-auto" />;
 */
export function useModalScrollFix<T extends HTMLElement>(
  scrollRef: React.RefObject<T | null>
): void {
  React.useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const handleWheel = (e: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = element;
      const maxScroll = scrollHeight - clientHeight;

      // Check if element is scrollable and can scroll in the wheel direction
      const isScrollable = maxScroll > 0;
      const canScrollUp = scrollTop > 0 && e.deltaY < 0;
      const canScrollDown = scrollTop < maxScroll && e.deltaY > 0;

      if (isScrollable && (canScrollUp || canScrollDown)) {
        // Manually scroll since Radix scroll-lock blocks native wheel scroll
        element.scrollTop += e.deltaY;
        e.stopPropagation();
        e.preventDefault();
      }
    };

    // Use non-passive listener to allow preventDefault
    element.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      element.removeEventListener('wheel', handleWheel);
    };
  }, [scrollRef]);
}
